import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CocktailIcon } from '@/components/Brand';
import { toast } from 'sonner';
import { CheckCircle, ArrowLeft } from '@phosphor-icons/react';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: username, 2: verify entry, 3: new password
  const [username, setUsername] = useState('');
  const [tips, setTips] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [lastEntryDate, setLastEntryDate] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submitStep1 = (e) => {
    e.preventDefault();
    setError('');
    if (!username.trim()) { setError('Enter your username.'); return; }
    setStep(2);
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    setError('');
    if (tips === '') {
      setError('Enter the tips from your most recent shift.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password/verify', {
        username: username.trim(),
        tips: Math.round(Number(tips)),
      });
      setResetToken(data.reset_token);
      setLastEntryDate(data.last_entry_date || '');
      setStep(3);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitStep3 = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 4) { setError('Password must be at least 4 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/forgot-password/reset', {
        reset_token: resetToken,
        new_password: password,
      });
      toast.success('Password reset. Sign in with your new password.');
      navigate('/login');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black pointer-events-none" />
      <div className="grain-overlay" />

      <div className="relative z-10 w-full max-w-md animate-fade-up">
        <div className="flex items-center gap-3 mb-10">
          <CocktailIcon className="text-[#D4AF37]" size={32} />
          <div>
            <div className="font-display text-2xl font-medium tracking-tight">kahrency</div>
            <div className="label-eyebrow">Reset password</div>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-2 mb-8" data-testid="forgot-progress">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                step >= n ? 'bg-[#D4AF37]' : 'bg-white/10'
              }`}
            />
          ))}
        </div>

        {step === 1 && (
          <>
            <h1 className="text-3xl sm:text-4xl font-display font-medium tracking-tighter mb-2">
              What&apos;s your username?
            </h1>
            <p className="text-slate-400 mb-8">We&apos;ll verify it&apos;s really you next.</p>
            <form onSubmit={submitStep1} className="space-y-6" data-testid="forgot-step1-form">
              <div>
                <Label className="label-eyebrow mb-2 block">Username</Label>
                <Input
                  data-testid="forgot-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  className="bg-black border-white/15 text-white h-12 focus:border-[#D4AF37]"
                  placeholder="johndoe"
                />
              </div>
              {error && (
                <div data-testid="forgot-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded px-3 py-2">
                  {error}
                </div>
              )}
              <Button type="submit" data-testid="forgot-step1-submit" className="w-full h-12 gold-btn">
                Continue
              </Button>
            </form>
          </>
        )}

        {step === 2 && (
          <>
            <h1 className="text-3xl sm:text-4xl font-display font-medium tracking-tighter mb-2">
              Prove it&apos;s you.
            </h1>
            <p className="text-slate-400 mb-8">
              Enter the <span className="text-white">tips</span> (whole dollars) from your <span className="text-[#D4AF37]">most recent shift</span>. If it matches what we have on file, you&apos;ll be able to reset your password.
            </p>
            <form onSubmit={submitStep2} className="space-y-5" data-testid="forgot-step2-form">
              <div>
                <Label className="label-eyebrow mb-2 block">Last shift · tips ($)</Label>
                <Input
                  data-testid="forgot-tips-input"
                  type="number"
                  inputMode="numeric"
                  step="1"
                  min="0"
                  value={tips}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, '');
                    setTips(v);
                  }}
                  className="bg-black border-white/15 text-white h-12 font-mono focus:border-[#D4AF37]"
                  placeholder="e.g. 142"
                />
              </div>
              {error && (
                <div data-testid="forgot-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded px-3 py-2">
                  {error}
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setStep(1); setError(''); }}
                  className="border-white/15 text-slate-300 hover:bg-white/5"
                  data-testid="forgot-back-btn"
                >
                  <ArrowLeft size={14} weight="bold" className="mr-2" /> Back
                </Button>
                <Button type="submit" data-testid="forgot-step2-submit" disabled={loading} className="flex-1 h-12 gold-btn">
                  {loading ? 'Verifying…' : 'Verify'}
                </Button>
              </div>
            </form>
          </>
        )}

        {step === 3 && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={22} weight="fill" className="text-emerald-400" />
              <span className="text-sm text-emerald-400 font-mono">Verified{lastEntryDate ? ` · ${lastEntryDate}` : ''}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-display font-medium tracking-tighter mb-2">
              Set a new password.
            </h1>
            <p className="text-slate-400 mb-8">Choose something at least 4 characters.</p>
            <form onSubmit={submitStep3} className="space-y-5" data-testid="forgot-step3-form">
              <div>
                <Label className="label-eyebrow mb-2 block">New password</Label>
                <Input
                  data-testid="forgot-new-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-black border-white/15 text-white h-12 focus:border-[#D4AF37]"
                />
              </div>
              <div>
                <Label className="label-eyebrow mb-2 block">Confirm new password</Label>
                <Input
                  data-testid="forgot-confirm-password-input"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="bg-black border-white/15 text-white h-12 focus:border-[#D4AF37]"
                />
              </div>
              {error && (
                <div data-testid="forgot-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded px-3 py-2">
                  {error}
                </div>
              )}
              <Button type="submit" data-testid="forgot-step3-submit" disabled={loading} className="w-full h-12 gold-btn">
                {loading ? 'Saving…' : 'Reset password'}
              </Button>
            </form>
          </>
        )}

        <div className="mt-10 pt-6 border-t border-white/5 text-sm text-slate-400">
          <Link to="/login" data-testid="forgot-to-login-link" className="text-[#D4AF37] hover:text-[#FDE047]">
            ← Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
